<script setup lang="ts">
  /**
   * Range field — 03-components/15c-range-field · OSCloud DS 1.1.166
   *
   * A form field whose control is a native range input: the platform keeps keyboard handling,
   * aria-valuenow and focus on the real control; track, fill and thumb are drawn on it through
   * its vendor pseudo-elements. The fill is a gradient stopped at --range-field-progress, set
   * here from the value. Ported without the DS's locked share and band-select modes (no consumer).
   */
  import { computed, useId, type Component } from 'vue'

  const props = withDefaults(
    defineProps<{
      modelValue: number
      label: string
      min: number
      max: number
      step?: number
      /** The readout beside the label and the control's aria-valuetext ("4 GB"). Defaults to the value. */
      valueText?: string
      /** The two bounds under the track. Default to min and max. */
      minLabel?: string
      maxLabel?: string
      /** A 16px BfIcon* before the label, in --bf-orange-dark. Pass it markRaw. */
      icon?: Component
      name?: string
      disabled?: boolean
      id?: string
    }>(),
    {
      step: 1,
      valueText: '',
      minLabel: '',
      maxLabel: '',
      icon: undefined,
      name: undefined,
      disabled: false,
      id: undefined,
    },
  )

  const emit = defineEmits<{ 'update:modelValue': [value: number] }>()

  const generatedId = useId()
  const controlId = computed(() => props.id ?? generatedId)
  const readout = computed(() => props.valueText || String(props.modelValue))
  const progress = computed(() => {
    const span = props.max - props.min
    return span > 0 ? `${((props.modelValue - props.min) / span) * 100}%` : '0%'
  })

  const onInput = (event: Event) => emit('update:modelValue', Number((event.target as HTMLInputElement).value))
</script>

<template>
  <div class="form-field range-field" :style="{ '--range-field-progress': progress }">
    <div class="range-field__label-row">
      <label class="form-field__label" :for="controlId">
        <component :is="icon" v-if="icon" class="range-field__label-icon" />
        {{ label }}
      </label>
      <output class="range-field__readout" :for="controlId" aria-live="off">{{ readout }}</output>
    </div>
    <div class="form-field__control">
      <input
        :id="controlId"
        class="range-field__input"
        type="range"
        :name="name"
        :min="min"
        :max="max"
        :step="step"
        :value="modelValue"
        :disabled="disabled"
        :aria-valuetext="readout"
        @input="onInput"
      />
    </div>
    <div class="range-field__scale" aria-hidden="true">
      <span>{{ minLabel || min }}</span>
      <span>{{ maxLabel || max }}</span>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .form-field {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .form-field__label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-2xs);
    font-weight: var(--bf-fw-bold);
    color: var(--bf-off-black);
    text-transform: uppercase;
    letter-spacing: var(--bf-tr-eyebrow);
  }

  .range-field {
    --range-field-track-height: 4px;
    --range-field-thumb-size: 18px;
    --range-field-progress: 0%;
  }

  /* Label row: the label on the left, the readout - the value the thumb points at - on the right,
     tabular so it does not jitter while dragging. */
  .range-field__label-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--bf-sp-2);
    margin-bottom: var(--bf-form-label-gap);
  }

  .range-field__label-icon {
    flex: 0 0 auto;
    color: var(--bf-orange-dark);
  }

  .range-field__readout {
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-xs_5);
    font-weight: var(--bf-fw-semibold);
    font-variant-numeric: tabular-nums;
    color: var(--bf-fg-1);
  }

  /* The input's height is the thumb's, so the row has a stable box while the track is 4px. */
  .range-field__input {
    -webkit-appearance: none;
    appearance: none;
    display: block;
    width: 100%;
    height: var(--range-field-thumb-size);
    margin: 0;
    padding: 0;
    background: transparent;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    -webkit-user-select: none;
    user-select: none;
  }

  .range-field__input:focus {
    outline: none;
  }

  /* Track: chosen (--bf-orange-dark) to the progress stop, free (--bf-line) to the end. */
  .range-field__input::-webkit-slider-runnable-track {
    height: var(--range-field-track-height);
    border-radius: var(--bf-r-pill);
    background: linear-gradient(
      to right,
      var(--bf-orange-dark) 0 var(--range-field-progress),
      var(--bf-line) var(--range-field-progress) 100%
    );
  }

  .range-field__input::-moz-range-track {
    height: var(--range-field-track-height);
    border-radius: var(--bf-r-pill);
    background: linear-gradient(
      to right,
      var(--bf-orange-dark) 0 var(--range-field-progress),
      var(--bf-line) var(--range-field-progress) 100%
    );
  }

  /* Thumb: white disc in a 3px orange ring, a tight control edge shadow. Centred on the track by
     a negative top margin (WebKit only; Gecko centres by itself). */
  .range-field__input::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: var(--range-field-thumb-size);
    height: var(--range-field-thumb-size);
    margin-top: calc((var(--range-field-track-height) - var(--range-field-thumb-size)) / 2);
    border: 3px solid var(--bf-orange-dark);
    border-radius: var(--bf-r-pill);
    background: var(--bf-form-control-surface);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
    transition:
      box-shadow var(--bf-dur-fast) var(--bf-ease),
      transform var(--bf-dur-fast) var(--bf-ease);
  }

  .range-field__input::-moz-range-thumb {
    width: var(--range-field-thumb-size);
    height: var(--range-field-thumb-size);
    border: 3px solid var(--bf-orange-dark);
    border-radius: var(--bf-r-pill);
    background: var(--bf-form-control-surface);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
    transition:
      box-shadow var(--bf-dur-fast) var(--bf-ease),
      transform var(--bf-dur-fast) var(--bf-ease);
  }

  /* The thumb answers the press - the only confirmation of the grab on touch. */
  .range-field__input:active:not(:disabled)::-webkit-slider-thumb {
    transform: scale(1.08);
  }

  .range-field__input:active:not(:disabled)::-moz-range-thumb {
    transform: scale(1.08);
  }

  /* Focus: the foundation's ring, on the thumb (a ring on the 4px track would read as a bar). */
  .range-field__input:focus-visible::-webkit-slider-thumb {
    box-shadow: var(--bf-form-focus-ring);
  }

  .range-field__input:focus-visible::-moz-range-thumb {
    box-shadow: var(--bf-form-focus-ring);
  }

  .range-field__input:disabled {
    opacity: var(--bf-form-disabled-opacity);
    cursor: not-allowed;
  }

  .range-field:has(:disabled) .range-field__readout,
  .range-field:has(:disabled) .range-field__scale {
    opacity: var(--bf-form-disabled-opacity);
  }

  /* Scale: min and max under the track. --bf-fg-2, not the DS's tertiary grey, which fails AA. */
  .range-field__scale {
    display: flex;
    justify-content: space-between;
    margin-top: 3px;
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-2xs);
    font-variant-numeric: tabular-nums;
    color: var(--bf-fg-2);
  }

  @media (prefers-reduced-motion: reduce) {
    .range-field__input:active:not(:disabled)::-webkit-slider-thumb,
    .range-field__input:active:not(:disabled)::-moz-range-thumb {
      transform: none;
    }
  }
</style>
